import { IGenericProposalsTableData } from '.';
import { IAbstractRecord } from '@models';
import { IListingData } from '@models/listing';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';

export function mapToListingData(data: IAbstractRecord): IListingData {
  const list: IAbstractRecord = {};

  data.Production.queries.getLatestProposalList.items.forEach(
    (obj: IGenericProposalsTableData, i: number) => {
      const subline =
        obj.production_Proposal_SublineID?.views?.PlanConfigManagement_all
          ?.properties;
      const line =
        obj.production_Proposal_LineID?.views?.PlanConfigManagement_all
          ?.properties;
      const plan =
        obj.production_Proposal_PlanID?.views?.PlanConfigManagement_all
          ?.properties;
      const insured =
        obj.production_Proposal_InsuredID?.views?.Production_all?.properties;

      list[i] = {
        id: obj.production_Proposal_Id,
        proposalNumber: obj.production_Proposal_InternalCode,
        name: obj.production_Proposal_ProposalName,
        line: `${line.ExternalCode} - ${line.Name}`,
        subline: `${subline.ExternalCode} - ${subline.Name}`,
        plan: `${plan.ExternalCode} - ${plan.Name}`,
        insured: insured?.FullName,
        status: capitalizeFirstLetter(
          obj.production_Proposal_Status?.replaceAll('_', ' ')
        ),
        statusReason: capitalizeFirstLetter(
          obj?.production_Proposal_StatusReason,
          '_'
        ),
        statusReasonTitle: capitalizeFirstLetter(
          obj?.production_Proposal_StatusReason,
          '_'
        ),
        effectiveDate: obj.production_Proposal_PolicyEffectiveDate,
      };
    }
  );

  return {
    pagedItems: list,
    pageSize: 10,
    pageNumber:
      data.Production.queries.getLatestProposalList?.paging?.pageNumber,
    totalCount:
      data.Production.queries.getLatestProposalList?.paging?.totalCount,
  };
}

export function staticToLovs(
  data: IAbstractRecord
): Record<string, Record<string, string>> {
  const lovs: Record<string, Record<string, string>> = {
    status: {},
    statusReason: {},
  };

  lovs.status = enumListAsRecordObject(
    data?.Production_ProposalStatuses?.enumValues
  );

  lovs.statusReason = lookupListAsRecordObject(
    data?.Production?.lookups?.proposalStatusReasons
  );
  return lovs;
}

export function dataToLovs(
  data: IAbstractRecord,
  pagination = false
): Record<string, Record<string, string>> {
  const lovs: Record<string, Record<string, string>> = {
    insured: {},
  };

  const items = pagination
    ? data?.Production?.queries?.GetAssignedPersonsWithPagination.items
    : data?.Production?.queries?.GetAssignedPersons;

  items?.forEach(
    (obj: {
      salesforceManagement_AssignedPerson_PersonID: { id: string };
      salesforceManagement_AssignedPerson_FullName: string;
      salesforceManagement_AssignedPerson_PersonCode: string;
    }) => {
      lovs.insured[obj.salesforceManagement_AssignedPerson_PersonID?.id] =
        `${obj.salesforceManagement_AssignedPerson_PersonCode} - ${obj.salesforceManagement_AssignedPerson_FullName}`;
    }
  );

  return lovs;
}

export function getBPPlanLovs(
  data: IAbstractRecord,
  filterV: IAbstractRecord
): Record<string, Record<string, string>> {
  const lovs: Record<string, Record<string, string>> = {
    line: {},
    subline: {},
    plan: {},
    businessPartner: {},
  };

  data?.SalesforceManagement?.queries?.getDistinctAssignedBPLineListWithoutPagination
    .sort((a: IAbstractRecord, b: IAbstractRecord) => {
      const codeA = Number(
        a.salesforceManagement_AssignedBPPlan_LineID.views
          .PlanConfigManagement_all.properties.ExternalCode
      );
      const codeB = Number(
        b.salesforceManagement_AssignedBPPlan_LineID.views
          .PlanConfigManagement_all.properties.ExternalCode
      );
      return codeA - codeB;
    })
    .forEach((obj: IAbstractRecord) => {
      const lineInfo = obj.salesforceManagement_AssignedBPPlan_LineID;
      lovs.line[lineInfo.id] =
        `${lineInfo.views.PlanConfigManagement_all.properties.ExternalCode} - ${lineInfo.views.PlanConfigManagement_all.properties.Name}`;
    });

  data?.SalesforceManagement?.queries?.getDistinctAssignedBPPlanListWithoutPagination
    .sort((a: IAbstractRecord, b: IAbstractRecord) => {
      const codeA =
        a.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.ExternalCode;
      const codeB =
        b.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.ExternalCode;
      return codeA.localeCompare(codeB);
    })
    ?.forEach((obj: IAbstractRecord) => {
      const planInfo = obj.salesforceManagement_AssignedBPPlan_PlanID;
      const planProperties = planInfo.views.PlanConfigManagement_all.properties;

      if (!filterV?.subline || filterV?.subline?.length === 0) {
        lovs.plan[planInfo.id] =
          `${planProperties.ExternalCode} - ${planProperties.Name}`;
      } else {
        if (filterV?.subline?.includes(planProperties.SublineID.id)) {
          lovs.plan[planInfo.id] =
            `${planProperties.ExternalCode} - ${planProperties.Name}`;
        }
      }
    });

  data?.SalesforceManagement?.queries?.getDistinctAssignedBPPlanListWithoutPagination
    .sort((a: IAbstractRecord, b: IAbstractRecord) => {
      const codeA =
        a.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.SublineID?.views
          ?.PlanConfigManagement_all?.properties?.ExternalCode;
      const codeB =
        b.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.SublineID?.views
          ?.PlanConfigManagement_all?.properties?.ExternalCode;
      return codeA.localeCompare(codeB);
    })
    ?.forEach((obj: IAbstractRecord) => {
      const planInfo = obj.salesforceManagement_AssignedBPPlan_PlanID;
      const planProperties = planInfo.views.PlanConfigManagement_all.properties;
      lovs.subline[planProperties.SublineID.id] =
        `${planProperties.SublineID.views.PlanConfigManagement_all.properties.ExternalCode} - ${planProperties.SublineID.views.PlanConfigManagement_all.properties.Name}`;
    });

  return lovs;
}

export function extractAssignedBPWithPagination(data: any) {
  const assignedBP: Record<string, string> = {};

  data.SalesforceManagement.queries.getAssignedBPList.items.forEach(
    (element: {
      businessPartner_Id: string;
      salesforceManagement_AssignedBP_BusinessPartnerID: {
        views: {
          SalesforceManagement_all: {
            properties: {
              Code: string;
              PersonID: {
                views: {
                  SalesforceManagement_all: {
                    properties: {
                      FullName: string;
                    };
                  };
                };
              };
            };
          };
        };
      };
    }) => {
      assignedBP[element.businessPartner_Id] =
        `${element.salesforceManagement_AssignedBP_BusinessPartnerID.views.SalesforceManagement_all.properties.Code} - ${element.salesforceManagement_AssignedBP_BusinessPartnerID.views.SalesforceManagement_all.properties.PersonID.views.SalesforceManagement_all.properties.FullName}`;
    }
  );

  return assignedBP;
}
